<template>
  <RCard class="additional-orders">
    <CreateOrderModal
      :showing="showModal"
      :entity="{ id: $route.params.id }"
      :namespace="namespace"
      @refresh="getAndSetTableRows"
      @close="showModal = false"
    />

    <AgGrid ref="agGrid" :options="gridOptions" :row-data="data" @cell-value-changed="cellValueChanged">
      <template #header>
        <Grid gaf="column" justify-content="space-between" align-items="center">
          <div class="text-subtitle-1 text-secondary-2 q-ml-sm q-mt-sm">ADDITIONAL ORDERS</div>

          <RBtn size="small" type="text" @click="showModal = true">Create new order</RBtn>
        </Grid>
      </template>
    </AgGrid>
  </RCard>
</template>

<script>
import * as apiTo from '@/modular-api'
import { agPriceCol, agStartDateCol } from '@/ag-grid/ag-columns'
// Library
import { cloneDeep, isEqual } from 'lodash'
// Components
import AgGrid from '@/ag-grid/AgGrid'
import CreateOrderModal from '@/reusable/CreateOrderModal'
import NestedFileDownloadLink from '@components/ag-grid-helpers/NestedFileDownloadLink'

export default {
  components: {
    AgGrid,
    CreateOrderModal
  },

  props: {
    namespace: {
      type: String,
      required: true
    }
  },

  data: () => ({
    data: null,
    showModal: false
  }),

  computed: {
    gridOptions() {
      return {
        columnDefs: [
          {
            headerName: 'File',
            cellRendererParams: params => {
              return {
                value: params.data
              }
            },
            cellRendererFramework: NestedFileDownloadLink
          },

          agStartDateCol({
            headerName: 'Date',
            field: 'date',
            editable: true,
            cellClass: ['editable-cell'],
            cellEditorFramework: 'DateEditor'
          }),

          {
            headerName: 'Purchase order reference',
            field: 'member_purchase_order_reference',
            editable: true,
            cellClass: ['editable-cell'],
            cellRenderer: params => params.value || 'Add reference',
            cellEditorFramework: 'TextEditor'
          },

          agPriceCol('Amount', 'amount', {
            editable: true,
            cellClass: ['editable-cell', 'ag-right-aligned-cell']
          }),

          {
            headerName: 'Actions',
            cellRendererFramework: 'ActionsCol',
            cellRendererParams: params => ({
              popupActions: [{ name: 'delete', label: 'Delete', textColorClass: 'text-danger-1' }],
              onPopupAction: () => {
                apiTo.DELETE_ENTITY_ADDITIONAL_ORDER(this.namespace, this.$route.params.id, params.data.id).then(() => {
                  this.data = this.data.filter(data => data.id !== params.data.id)
                })
              }
            })
          }
        ],
        defaultColDef: {
          resizable: false,
          floatingFilter: false
        },
        domLayout: 'autoHeight'
      }
    }
  },

  created() {
    this.getAndSetTableRows()
  },

  methods: {
    getAndSetTableRows() {
      apiTo
        .GET_ADDITIONAL_ORDERS_FOR_ENTITY(this.namespace, this.$route.params.id)
        .then(({ data }) => (this.data = data.result))
    },

    cellValueChanged({ newValue, column, oldValue, data }) {
      const newVal = cloneDeep(newValue) || null
      const oldVal = cloneDeep(oldValue) || null
      if (isEqual(newVal, oldVal)) return

      const updatedData = cloneDeep(data)
      updatedData.date = this.formatDateForPost(updatedData.date, [this.settings.date_format, 'YYYY-MM-DD'])

      this.updateTable(updatedData, column, oldValue)
    },

    updateTable(updatedData, column, oldValue) {
      apiTo
        .UPDATE_ENTITY_ADDITIONAL_ORDER(this.namespace, this.$route.params.id, updatedData.id, updatedData)
        .then(({ data }) => {
          this.$refs.agGrid.updateRow(data.result.id, data.result)
        })
        .catch(() => {
          this.$refs.agGrid.updateRow(updatedData.id, {
            ...updatedData,
            [column.colId]: oldValue
          })
        })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/mixins';

.additional-orders {
  @include table-with-no-filters;
}
</style>
